import { isAutoCalculable, normaliseLaborMethod } from './laborMethods';
/**
 * resolveLaborCost.js — B5 (Iter-20): labor cost resolution with precedence chain.
 *
 * LINE LABOR COST resolution, in order:
 *   1. sub-area OVERRIDE  (inheritsDefault === false && laborMethod set)
 *      → rate × quantity (piece/linear only; hourly/day deferred to Sarah ruling)
 *   2. Margins & Labor DEFAULT  (inheritsDefault === true, area default exists)
 *      → rate × quantity
 *   3. PRODUCT CATALOG  (laborCostPerUnit × quantity)
 *   4. zero  (no source — do not silently emit 0)
 *
 * THEN, unchanged:
 *   cost_basis = materialCost + laborCost + additionalLabor + additionalCost
 *   lineTotal  = cost_basis / (1 − effectiveMargin)
 *
 * NOTE: changing a Margins & Labor default re-prices FUTURE lines in every
 * inheriting sub-area. Existing lines are safe — they store `laborCost` and
 * the margin engine reads the stored value (not re-resolving at render time).
 *
 * @module quote-builder/resolveLaborCost
 */

/**
 * Resolve labor cost for a line item.
 *
 * @param {object} params
 * @param {number} params.quantity - The line's quantity (sqft, linear ft, etc.)
 * @param {object|null} params.subArea - The sub-area record (with laborMethod, laborRate, laborUnit, inheritsDefault)
 *   laborRate is in DOLLARS (e.g. 1.25 = $1.25/unit) — same convention as additionalLabor.
 * @param {object|null} params.areaDefault - The area-level default from Margins & Labor settings
 *   Shape: { rate, rateType } — rate is in DOLLARS (e.g. "1" from labor_rates.per_sq_ft = $1.00)
 * @param {object|null} params.product - The product from the catalog (with laborCostPerUnit)
 *   laborCostPerUnit is in CENTS (e.g. 90 = 90¢/unit) — catalog convention.
 * @returns {{ laborCost: number, laborSource: string }}
 *   laborCost in CENTS. laborSource: 'sub_area' | 'area_default' | 'catalog' | 'none'
 */
export function resolveLaborCost({ quantity, subArea, areaDefault, product }) {
  const qty = Number(quantity) || 0;
  if (qty <= 0) return { laborCost: 0, laborSource: 'none' };

  // Tier 1: sub-area override
  // laborRate is DOLLARS — convert to cents before multiplying.
  // Normalise legacy laborMethod via the canonical function.
  const normMethod = normaliseLaborMethod(subArea?.laborMethod);
  if (subArea && subArea.inheritsDefault === false && normMethod && Number(subArea.laborRate) > 0) {
    const rateDollars = Number(subArea.laborRate);
    if (isAutoCalculable(normMethod)) {
      return {
        laborCost: Math.round(rateDollars * 100 * qty),
        laborSource: 'sub_area',
      };
    }
    // Hourly/day: not auto-calculable from a sqft quantity. Fall through.
  }

  // Tier 2: Margins & Labor area default
  // rate is DOLLARS (from labor_rates.per_sq_ft etc.) — convert to cents.
  // Guard: an unset or zero rate must not shadow the catalog.
  if (subArea?.inheritsDefault !== false && areaDefault && Number(areaDefault.rate) > 0) {
    const rateDollars = Number(areaDefault.rate);
    if (isAutoCalculable(areaDefault.rateType)) {
      return {
        laborCost: Math.round(rateDollars * 100 * qty),
        laborSource: 'area_default',
      };
    }
    // Hourly/day default: not auto-calculable. Fall through.
  }

  // Tier 3: product catalog
  // laborCostPerUnit is already in CENTS — no conversion needed.
  if (product && product.laborCostPerUnit != null) {
    const rateCents = Number(product.laborCostPerUnit) || 0;
    return {
      laborCost: Math.round(rateCents * qty),
      laborSource: 'catalog',
    };
  }

  // Tier 4: no source
  return { laborCost: 0, laborSource: 'none' };
}

